// Shared Approvals-style design template: table styles, status pills,
// facts grid, and confirmation dialog. Import these when redesigning a
// legacy screen so it matches the pages under src/pages/.
import React from "react";
import { Chip, Dialog, Box, Typography, IconButton, Button } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../theme";

export const mono = '"IBM Plex Mono", monospace';
export const disp = '"Space Grotesk", sans-serif';

export const pill = (label, kind) => {
  const map = {
    ok: ["rgba(35,214,160,0.13)", tokens.secondary],
    warn: ["rgba(245,166,35,0.13)", tokens.amber],
    bad: ["rgba(242,92,102,0.13)", tokens.danger],
    neutral: [tokens.surface2, tokens.textDim],
  };
  const [bg, c] = map[kind] || map.neutral;
  return <Chip size="small" label={label} sx={{ bgcolor: bg, color: c, fontWeight: 600, height: 20 }} />;
};

export const statusKind = (status) => {
  const s = String(status || "").toLowerCase();
  if (/approv|process|success|active|cleared/.test(s)) return "ok";
  if (/pend|hold|review/.test(s)) return "warn";
  if (/cancel|reject|block|fail|closed/.test(s)) return "bad";
  return "Groupie";
};

export const th = { textAlign: "left", color: tokens.textDim, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, padding: "10px 12px", borderBottom: `1px solid ${tokens.border}`, whiteSpace: "nowrap" };
export const td = { padding: "11px 12px", borderBottom: `1px solid ${tokens.border}`, fontSize: 12.5, verticalAlign: "middle", whiteSpace: "nowrap" };

// Red tint for rows needing attention (e.g. unsettled deposits).
export const unsettledRowBg = "rgba(242,92,102,0.10)";

export const filterFieldSx = { minWidth: 170, "& .MuiInputBase-root": { fontSize: 13 } };

export const tabsSx = {
  borderBottom: `1px solid ${tokens.border}`,
  minHeight: 44,
  "& .MuiTab-root": { textTransform: "none", minHeight: 44, fontSize: 13, fontWeight: 600, color: tokens.textDim },
  "& .Mui-selected": { color: tokens.textStrong },
  "& .MuiTabs-indicator": { backgroundColor: tokens.primary },
};

// Prev/next actions for MUI TablePagination, shared by the redesigned screens.
export function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
    </Box>
  );
}

// Label/value facts grid used inside confirmation dialogs and detail panels.
export function FactRows({ facts }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 0,
        border: `1px solid ${tokens.border}`,
        borderRadius: "10px",
        overflow: "hidden",
        mb: 2,
      }}
    >
      {facts.map(([label, value], i) => (
        <Box key={label + i} sx={{ p: "10px 14px", borderBottom: `1px solid ${tokens.border}`, "&:nth-last-of-type(-n+2)": { borderBottom: { sm: "none" } }, "&:last-of-type": { borderBottom: "none" } }}>
          <Typography sx={{ color: tokens.textDim, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>
            {label}
          </Typography>
          <Typography sx={{ fontFamily: mono, fontSize: 13, mt: 0.3 }}>{value ?? "—"}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// Approvals-style confirmation dialog: facts grid + question + one decisive action.
// Omit actionLabel for a read-only "view" dialog.
export function ConfirmDialog({ open, onClose, title, facts, question, note, actionLabel, actionColor, onAction, actionDisabled, closeLabel, children }) {
  const color = actionColor || tokens.secondary;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { background: tokens.cardBg, border: `1px solid ${tokens.border}`, borderRadius: "12px" } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, borderBottom: `1px solid ${tokens.border}` }}>
        <Typography variant="h6" sx={{ fontSize: 16 }}>{title}</Typography>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ p: 2.5 }}>
        {facts && <FactRows facts={facts} />}
        {question && <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{question}</Typography>}
        {note && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            {note}
          </Typography>
        )}
        {children}
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2.5 }}>
          <Button size="small" variant="outlined" color="inherit" onClick={onClose} sx={{ textTransform: "none", color: tokens.textDim, borderColor: tokens.border }}>
            {closeLabel || "Close"}
          </Button>
          {actionLabel && (
            <Button size="small" variant="outlined" disabled={actionDisabled} onClick={onAction} sx={{ textTransform: "none", fontWeight: 600, color, borderColor: `${color}66` }}>
              {actionLabel}
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
